import React, { useRef, useState } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useOutsideClick,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';

import { WATTWATCHERS_MANUFACTURER_ID } from 'clipsal-cortex-utils/src/constants/common-constants';

import { debounceEvent } from '../../../../utils/component-helpers';
import { SiteMeterData } from './meter-config-types';
import { useSearchMeters, useSearchSites } from './meterConfigApi';

type SearchType = 'site' | 'meter';

const SEARCH_FILTER_OPTIONS = [
  { label: 'Site Id/Name', value: 'site' },
  { label: 'Serial/Site Identifier', value: 'meter' },
];

const SEARCH_FILTER_VALUE_TO_LABEL = {
  site: 'Site ID or Name',
  meter: 'Serial or Site Identifier',
};

const INITIAL_METER_SEARCH_STATE = {
  isResultMenuOpen: false,
  searchType: 'site' as SearchType,
  searchTerm: '',
};

type MeterSearchFormProps = {
  selectedMeter: SiteMeterData;
  onUpdateSelectedMeter: (meter: SiteMeterData) => void;
};

export const MeterSearchForm = ({ selectedMeter, onUpdateSelectedMeter }: MeterSearchFormProps) => {
  const [urlParams, setParams] = useSearchParams();
  const [{ searchType, searchTerm, isResultMenuOpen }, setState] = useState({
    ...INITIAL_METER_SEARCH_STATE,
    searchType: (urlParams.get('siteIdentifier') ? 'meter' : 'site') as SearchType,
  });
  const {
    searchResults: siteResults,
    isLoading: isSearchingSites,
    isFetching: isFetchingSites,
  } = useSearchSites(searchTerm, searchType === 'meter');
  const {
    data: meterResults,
    isLoading: isSearchingMeters,
    isFetching: isFetchingMeters,
  } = useSearchMeters(searchTerm, searchType === 'site');
  const isSearching =
    searchType === 'site' ? isSearchingSites || isFetchingSites : isSearchingMeters || isFetchingMeters;
  const searchResults = searchType === 'site' ? siteResults : meterResults;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  useOutsideClick({
    ref: tableRef as React.RefObject<HTMLTableElement>,
    handler: () => setState((prevState) => ({ ...prevState, isResultMenuOpen: false })),
  });
  const isMobileViewport = useBreakpointValue({ base: true, xl: false }, { ssr: false });

  const handleSearch = React.useMemo(
    () =>
      debounceEvent(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = e.target.value.trim();
        setState((prevState) => ({ ...prevState, searchTerm, isResultMenuOpen: !!searchTerm }));
      }, 600),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchType]
  );

  const handleSelectMeter = (newMeter: SiteMeterData) => {
    if (!newMeter.siteDeviceId) return;

    if (selectedMeter.siteDeviceId === newMeter.siteDeviceId) {
      setState((prevState) => ({ ...prevState, isResultMenuOpen: false }));
      return;
    }

    if (inputRef.current) inputRef.current.value = searchType === 'meter' ? newMeter.siteIdentifier : newMeter.siteName;

    // update the URL
    setParams((searchParams) => {
      searchParams.set('siteId', String(newMeter.siteId));
      searchParams.set('siteName', newMeter.siteName);
      searchParams.set('serialNumber', newMeter.serialNumber);
      searchParams.set('siteDeviceId', String(newMeter.siteDeviceId));
      searchParams.set('siteIdentifier', newMeter.siteIdentifier);
      searchParams.set('manufacturerId', String(newMeter.manufacturerId));
      searchParams.set('model', newMeter.model);
      searchParams.set('manufacturerName', newMeter.manufacturerName);
      return searchParams;
    });

    onUpdateSelectedMeter(newMeter);

    setState((prevState) => ({
      ...prevState,
      isResultMenuOpen: false,
    }));
  };

  return (
    <Flex
      align={'center'}
      mx={4}
      mt={2}
      direction={isMobileViewport ? 'column' : 'row'}
      data-testid="meter-search-form"
    >
      <FormControl maxW={isMobileViewport ? undefined : '400px'} zIndex={2}>
        <FormLabel>Search by {SEARCH_FILTER_VALUE_TO_LABEL[searchType]}</FormLabel>
        <InputGroup size="md" position="relative" ref={tableRef}>
          <Input
            data-testid={'meter-search-input'}
            ref={inputRef}
            defaultValue={urlParams.get('siteIdentifier') ?? ''}
            onFocus={() => setState((prevState) => ({ ...prevState, isResultMenuOpen: !!searchResults.length }))}
            onChange={handleSearch}
            py={'20px'}
            placeholder={`Enter ${SEARCH_FILTER_VALUE_TO_LABEL[searchType]}`}
          />
          <InputRightElement height="100%">
            {isSearching ? (
              <Spinner my="auto" color="gray.500" size="sm" />
            ) : (
              isResultMenuOpen && (
                <IconButton
                  ref={closeButtonRef}
                  aria-label="Reset Search"
                  variant={'ghost'}
                  icon={<CloseIcon w={2} h={2} />}
                  onClick={() => {
                    if (inputRef?.current) inputRef.current.value = '';
                    setState((prevState) => ({
                      ...INITIAL_METER_SEARCH_STATE,
                      searchType: prevState.searchType,
                    }));
                  }}
                />
              )
            )}
          </InputRightElement>

          {isResultMenuOpen && searchTerm && (
            <TableContainer
              shadow=" rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"
              bg={'white'}
              _dark={{ bg: 'gray.900' }}
              borderRadius="0px 0px 10px 10px"
              position="absolute"
              top={43}
              left={0}
              zIndex={1}
              w={'100%'}
              data-testid="search-results-table"
            >
              {!searchResults.length && (
                <Text py={12} align="center">
                  {isSearching ? 'Searching...' : 'No search results found. Please try again.'}
                </Text>
              )}

              {!!searchResults.length && (
                <Table variant="simple" size="sm" colorScheme="blue">
                  <Thead>
                    <Tr>
                      <Th border={'0px !important'} fontSize="13px" pt={2}>
                        Site ID
                      </Th>
                      <Th border={'0px !important'} fontSize="13px">
                        Site Name
                      </Th>
                      <Th border={'0px !important'} fontSize="13px">
                        Serial Number
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {searchResults.map((meter, idx) => {
                      const { siteIdentifier, siteName, siteId, manufacturerId, serialNumber, siteDeviceId } = meter;
                      const isWattwatchersMeter = manufacturerId === WATTWATCHERS_MANUFACTURER_ID;
                      const isSelected = siteDeviceId === selectedMeter.siteDeviceId;

                      return (
                        <Tr
                          data-testid={`search-result-tr-${idx}`}
                          key={(siteName ?? 'site-') + idx}
                          position="relative"
                          bg={isSelected ? 'green.50' : undefined}
                          _hover={{
                            bg: 'blue.50',
                            cursor: 'pointer',
                          }}
                          _dark={{
                            bg: isSelected ? 'green.900' : undefined,
                            _hover: {
                              bg: 'blue.900',
                            },
                          }}
                          onClick={() => handleSelectMeter(meter)}
                        >
                          <Td border={'0px !important'}>{siteId || 'N/A'}</Td>
                          <Td border={'0px !important'} maxW="120px" isTruncated>
                            {siteName || 'N/A'}
                          </Td>
                          <Td border={'0px !important'}>
                            {(isWattwatchersMeter ? siteIdentifier : serialNumber) ?? 'No Valid Meter'}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              )}
            </TableContainer>
          )}
        </InputGroup>
      </FormControl>
      <FormControl
        maxW={isMobileViewport ? undefined : '200px'}
        ml={isMobileViewport ? 0 : 4}
        mt={isMobileViewport ? 2 : 0}
      >
        <FormLabel>Search Filter</FormLabel>
        <Select
          data-testid="search-filter-select"
          value={searchType}
          onChange={(event) => {
            const searchType = event.target.value as SearchType;
            if (inputRef?.current) inputRef.current.value = '';
            setState((prevState) => ({
              ...prevState,
              searchType,
              searchTerm: '',
            }));
          }}
        >
          {SEARCH_FILTER_OPTIONS.map(({ label, value }) => (
            <option value={value} key={value} data-testid={value}>
              {label}
            </option>
          ))}
        </Select>
      </FormControl>
    </Flex>
  );
};
